import { inject, Injectable } from '@angular/core';
import { ID } from '@datorama/akita';
import { switchMap, tap } from 'rxjs/operators';

import { GqlService } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';

import { MilestoneModel, MilestoneStore } from './milestone/milestone.store';
import { MilestoneCategoryModel, MilestoneCategoryStore } from './milestone-category.store';

@Injectable({ providedIn: 'root' })
export class MilestoneCategoryService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private milestoneCategoryStore = inject(MilestoneCategoryStore);
  private milestoneStore = inject(MilestoneStore);
  private overlayService = inject(OverlayService);

  add(milestoneCategory: MilestoneCategoryModel) {
    this.milestoneCategoryStore.add(milestoneCategory);
  }

  addMilestoneToCategory(cat_id: string, milestone_id: string) {
    this.milestoneCategoryStore.update(cat_id, (state) => {
      return {
        milestone_ids: [...state.milestone_ids, milestone_id],
      };
    });
  }

  get() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.milestoneCategoryStore.setLoading(true);
        this.milestoneStore.setLoading(true);

        return this.gqlService.getMilestonesAndCategories$().pipe(
          tap(({ data, errors, success }) => {
            if (success && data) {
              this.milestoneCategoryStore.set(
                data.map(({ __typename, description, grouping_order, id, milestones, name }) => {
                  return {
                    __typename,
                    description,
                    grouping_order,
                    id,
                    milestone_ids: (milestones || []).map((milestone) => milestone?.id) as string[],
                    name,
                  };
                })
              );

              const milestoneArr: MilestoneModel[] = [];

              data.forEach(({ id: c_id, milestones }) => {
                (milestones || []).forEach((milestone) => {
                  if (milestone) {
                    const { __typename, description, id: m_id, name } = milestone;

                    milestoneArr.push({
                      __typename,
                      description,
                      id: m_id,
                      milestone_category_id: c_id,
                      name,
                    });
                  }
                });
              });
              this.milestoneStore.set(milestoneArr);
            } else {
              this.overlayService.error(errors);
            }

            this.milestoneCategoryStore.setLoading(false);
            this.milestoneStore.setLoading(false);
          })
        );
      })
    );
  }

  remove(id: ID) {
    this.milestoneCategoryStore.remove(id);
  }

  update(id: string, milestoneCategory: Partial<MilestoneCategoryModel>) {
    this.milestoneCategoryStore.update(id, milestoneCategory);
  }
}
